/**
 * Image Element Schema
 */

import type { ElementSchema, MediaFieldSchema, TextFieldSchema, SelectFieldSchema } from '@ai-content-hub/content-kit';

export const defaultImageElementI18n = {
    en: {
        image: 'Image',
        altText: 'Alt text',
        uploadImage: 'Upload image',
        describeImage: 'Describe the image...',
        caption: 'Caption',
        imageCaptionPlaceholder: 'Image caption...',
        size: 'Size',
        alignment: 'Alignment',
        alignLeft: 'Left',
        alignCenter: 'Center',
        alignRight: 'Right',
        sizeSmall: 'Small (25%)',
        sizeMedium: 'Medium (50%)',
        sizeLarge: 'Large (75%)',
        sizeFull: 'Full Width (100%)',
    },
    vi: {
        image: 'Hình ảnh',
        altText: 'Văn bản thay thế',
        uploadImage: 'Tải ảnh lên',
        describeImage: 'Mô tả hình ảnh...',
        caption: 'Chú thích',
        imageCaptionPlaceholder: 'Chú thích ảnh...',
        size: 'Kích thước',
        alignment: 'Căn chỉnh',
        alignLeft: 'Trái',
        alignCenter: 'Giữa',
        alignRight: 'Phải',
        sizeSmall: 'Nhỏ (25%)',
        sizeMedium: 'Vừa (50%)',
        sizeLarge: 'Lớn (75%)',
        sizeFull: 'Toàn chiều rộng (100%)',
    },
};

export interface ImageElementData {
    src: string;
    alt: string;
    caption?: string;
    size: 'small' | 'medium' | 'large' | 'full';
    alignment: 'left' | 'center' | 'right';
}

export const imageElementSchema: ElementSchema<ImageElementData> = {
    type: 'element-image',
    title: 'Image',
    description: 'Image content with caption and styling options',
    version: '1.0.0',
    category: 'media',
    icon: 'Image',
    tags: ['image', 'media', 'photo', 'picture'],
    fields: [
        {
            name: 'src',
            type: 'media',
            label: 'Image',
            description: 'Upload or enter image URL',
            required: true,
            mediaType: 'image',
            accept: ['image/*'],
        } as MediaFieldSchema,
        {
            name: 'alt',
            type: 'text',
            label: 'Alt Text',
            description: 'Description for accessibility',
            placeholder: 'Describe the image...',
            required: true,
        } as TextFieldSchema,
        {
            name: 'caption',
            type: 'text',
            label: 'Caption',
            description: 'Optional caption below the image',
            placeholder: 'Image caption...',
        } as TextFieldSchema,
        {
            name: 'size',
            type: 'select',
            label: 'Size',
            defaultValue: 'medium',
            options: [
                { value: 'small', label: 'Small (25%)' },
                { value: 'medium', label: 'Medium (50%)' },
                { value: 'large', label: 'Large (75%)' },
                { value: 'full', label: 'Full Width (100%)' },
            ],
        } as SelectFieldSchema,
        {
            name: 'alignment',
            type: 'select',
            label: 'Alignment',
            defaultValue: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        } as SelectFieldSchema,
    ],
    defaultData: {
        src: '',
        alt: '',
        caption: '',
        size: 'medium',
        alignment: 'center',
    },
    i18n: defaultImageElementI18n,
};

